/**
 * Firebase Web SDK wrapper — Google Sign-In + ID token.
 *
 * Inisialisasi lazy: hanya jika user klik tombol login.
 * Config dari VITE_FIREBASE_* env vars.
 */

import { initializeApp, type FirebaseApp } from "firebase/app";
import {
  GoogleAuthProvider,
  getAuth,
  signInWithPopup,
  signOut as fbSignOut,
  onAuthStateChanged,
  type User as FbUser,
} from "firebase/auth";
import type { AuthUser } from "@/types";

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
};

let app: FirebaseApp | null = null;
const provider = new GoogleAuthProvider();
provider.setCustomParameters({ prompt: "select_account" });

function getApp(): FirebaseApp {
  if (!app) {
    if (!firebaseConfig.apiKey) {
      throw new Error(
        "Firebase belum dikonfigurasi. Set VITE_FIREBASE_* di .env.local",
      );
    }
    app = initializeApp(firebaseConfig);
  }
  return app;
}

export function onAuthChange(callback: (user: FbUser | null) => void): () => void {
  const auth = getAuth(getApp());
  return onAuthStateChanged(auth, callback);
}

/**
 * Terjemahan kode error Firebase → pesan yang bisa ditindaklanjuti user.
 * Tanpa ini, kegagalan login hanya muncul sebagai string teknis (atau tidak
 * muncul sama sekali) dan mustahil didiagnosis dari UI.
 */
function describeAuthError(code: string): string | null {
  switch (code) {
    case "auth/unauthorized-domain":
      return `Domain "${window.location.hostname}" belum terdaftar di Firebase. Buka Firebase Console → Authentication → Settings → Authorized domains, tambahkan domain ini (tanpa port, tanpa https://), lalu ulangi.`;
    case "auth/popup-blocked":
      return "Popup login diblokir browser. Izinkan popup untuk situs ini lalu coba lagi.";
    case "auth/popup-closed-by-user":
      return "Jendela login ditutup sebelum proses selesai.";
    case "auth/cancelled-popup-request":
      return "Ada permintaan login lain yang sedang berjalan. Coba lagi.";
    case "auth/operation-not-allowed":
      return "Provider Google belum diaktifkan di Firebase Console → Authentication → Sign-in method.";
    case "auth/network-request-failed":
      return "Gagal menghubungi Firebase. Cek koneksi internet atau VPN Anda.";
    default:
      return null;
  }
}

export async function signInWithGoogle(): Promise<AuthUser> {
  const auth = getAuth(getApp());

  let result;
  try {
    result = await signInWithPopup(auth, provider);
  } catch (e) {
    const code = (e as { code?: string }).code ?? "";
    const friendly = describeAuthError(code);
    if (friendly) throw new Error(friendly);
    throw e;
  }

  const fbUser = result.user;
  const idToken = await fbUser.getIdToken();
  return {
    uid: fbUser.uid,
    email: fbUser.email ?? `${fbUser.uid}@firebase.local`,
    role: "user", // akan di-update oleh syncUser setelah hit backend
    idToken,
  };
}

export async function signOutCurrent(): Promise<void> {
  const auth = getAuth(getApp());
  await fbSignOut(auth);
}

export async function getFreshIdToken(): Promise<string | null> {
  const auth = getAuth(getApp());
  const fbUser = auth.currentUser;
  if (!fbUser) return null;
  return fbUser.getIdToken(/* forceRefresh */ false);
}